// src/components/public_utilities/pu/PuPrimitives.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities v2 building blocks (FlowSense teal styling)
// --- UPDATE (Public Utilities v2) — NEW FILE ------------------------------------------------
//   PuCard      section card with gradient bar, icon tile, title, subtitle and actions
//   KpiTile     compact stat tile (value "—" when unknown, optional hint + click)
//   EmptyState  friendly empty / unavailable message
//   BarList     horizontal bars (label · value · share) for distributions
// --- END UPDATE ------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, HStack, VStack, Heading, Text, Icon, Circle, Divider, Tooltip, Progress } from '@chakra-ui/react';
import { usePuColors, fmtNum } from './puUtils';

export const PuCard = React.memo(({ icon, title, subtitle, actions, children, noPadding = false, ...rest }) => {
    const c = usePuColors();
    return (
        <Box bg={c.cardBg} borderRadius="2xl" boxShadow="lg" borderWidth="1px" borderColor={c.border} overflow="hidden" {...rest}>
            <Box h="3px" bgGradient={c.gradient} />
            {(title || actions) && (
                <>
                    <Flex align="center" justify="space-between" px={{ base: 4, md: 6 }} pt={4} pb={3} flexWrap="wrap" gap={3}>
                        <HStack spacing={3} minW={0}>
                            {icon && (
                                <Box p={2} bgGradient={c.gradient} borderRadius="xl" shadow="sm" flexShrink={0}>
                                    <Icon as={icon} boxSize={5} color="white" />
                                </Box>
                            )}
                            <VStack align="start" spacing={0} minW={0}>
                                <Heading size="sm" color={c.text} letterSpacing="-0.01em" noOfLines={1}>{title}</Heading>
                                {subtitle && <Text fontSize="xs" color={c.muted} noOfLines={2}>{subtitle}</Text>}
                            </VStack>
                        </HStack>
                        {actions && <HStack spacing={2} flexWrap="wrap">{actions}</HStack>}
                    </Flex>
                    <Divider borderColor={c.border} />
                </>
            )}
            <Box p={noPadding ? 0 : { base: 4, md: 6 }}>{children}</Box>
        </Box>
    );
});
PuCard.displayName = 'PuCard';
PuCard.propTypes = { icon: PropTypes.elementType, title: PropTypes.node, subtitle: PropTypes.node, actions: PropTypes.node, children: PropTypes.node, noPadding: PropTypes.bool };

export const KpiTile = React.memo(({ label, value, icon, scheme = 'teal', hint, onClick, suffix }) => {
    const c = usePuColors();
    const shown = typeof value === 'number' ? fmtNum(value) : (value ?? '—');
    const body = (
        <Box bg={c.cardBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={c.border} shadow="sm" transition="all 0.15s"
            cursor={onClick ? 'pointer' : 'default'} _hover={onClick ? { shadow: 'md', transform: 'translateY(-1px)', borderColor: `${scheme}.300` } : undefined}
            onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}>
            <HStack spacing={3} align="center">
                {icon && <Circle size="36px" bg={`${scheme}.100`} flexShrink={0}><Icon as={icon} boxSize={4} color={`${scheme}.600`} /></Circle>}
                <VStack align="start" spacing={0} minW={0}>
                    <Text fontSize="xs" color={c.muted} fontWeight="600" noOfLines={1}>{label}</Text>
                    <HStack spacing={1} align="baseline">
                        <Text fontSize="xl" fontWeight="800" color={c.text} lineHeight="1.2">{shown}</Text>
                        {suffix && shown !== '—' && <Text fontSize="xs" color={c.muted}>{suffix}</Text>}
                    </HStack>
                </VStack>
            </HStack>
        </Box>
    );
    return hint ? <Tooltip label={hint} hasArrow openDelay={300}>{body}</Tooltip> : body;
});
KpiTile.displayName = 'KpiTile';
KpiTile.propTypes = { label: PropTypes.node.isRequired, value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), icon: PropTypes.elementType, scheme: PropTypes.string, hint: PropTypes.string, onClick: PropTypes.func, suffix: PropTypes.string };

export const EmptyState = ({ icon, title, children }) => {
    const c = usePuColors();
    return (
        <VStack py={8} spacing={2} color={c.muted} textAlign="center">
            {icon && <Icon as={icon} boxSize={8} opacity={0.6} />}
            <Text fontWeight="700" fontSize="sm">{title}</Text>
            {children && <Text fontSize="xs" maxW="420px">{children}</Text>}
        </VStack>
    );
};
EmptyState.propTypes = { icon: PropTypes.elementType, title: PropTypes.node, children: PropTypes.node };

export const BarList = React.memo(({ rows, colorScheme = 'teal', onSelect, max }) => {
    const c = usePuColors();
    const top = max || Math.max(1, ...rows.map((r) => Number(r.value) || 0));
    return (
        <VStack align="stretch" spacing={2}>
            {rows.map((r) => (
                <Box key={r.key ?? r.label} cursor={onSelect ? 'pointer' : 'default'} onClick={onSelect ? () => onSelect(r) : undefined}
                    _hover={onSelect ? { bg: c.hoverBg } : undefined} borderRadius="md" px={1} py={0.5}>
                    <Flex justify="space-between" fontSize="xs" mb={1}>
                        <Text color={c.text} fontWeight="600" noOfLines={1}>{r.label}</Text>
                        <Text color={c.muted} flexShrink={0} ml={2}>{fmtNum(r.value)}{r.extra ? ` · ${r.extra}` : ''}</Text>
                    </Flex>
                    <Progress value={((Number(r.value) || 0) / top) * 100} size="xs" borderRadius="full" colorScheme={r.scheme || colorScheme} />
                </Box>
            ))}
        </VStack>
    );
});
BarList.displayName = 'BarList';
BarList.propTypes = { rows: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.node, value: PropTypes.number })).isRequired, colorScheme: PropTypes.string, onSelect: PropTypes.func, max: PropTypes.number };
